import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle } from "lucide-react";
import { Logo } from "@/components/Logo";
import { GoogleButton } from "@/components/GoogleButton";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success(`Welcome back, ${u.name || "driver"}`);
      const map = { sender:"/sender", lorry_owner:"/owner", driver:"/driver", admin:"/admin", super_admin:"/superadmin", accounts:"/accounts", loading_supervisor:"/ops" };
      nav(map[u.role] || "/");
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      <div className="hidden lg:flex flex-1 relative border-r border-[#2A2A2A] asphalt-grid">
        <div className="m-auto p-12 max-w-lg">
          <Link to="/" className="inline-flex mb-12"><Logo /></Link>
          <div className="overline text-[#FF5A00]">WELCOME BACK</div>
          <h1 className="font-display font-black text-5xl mt-4 tracking-tight">
            Log in.<br />Move loads.<br /><span className="text-[#FF5A00]">Grow money.</span>
          </h1>
          <p className="mt-6 text-zinc-400 leading-relaxed">
            Your bids, chats and active shipments are waiting on the other side. Powered by Northline Logistics.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <form onSubmit={onSubmit} className="w-full max-w-md" data-testid="login-form">
          <div className="lg:hidden mb-8"><Logo /></div>
          <div className="overline text-zinc-500">SIGN IN</div>
          <h2 className="font-display font-black text-3xl mt-2">Enter the yard</h2>

          <div className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email" className="overline text-zinc-400">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-2 h-12 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FF5A00] rounded-sm"
                placeholder="you@company.in"
              />
            </div>
            <div>
              <Label htmlFor="password" className="overline text-zinc-400">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-2 h-12 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FF5A00] rounded-sm"
                placeholder="••••••••"
              />
            </div>

            {err ? (
              <div className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-900/40 text-red-300 text-sm rounded-sm" data-testid="login-error">
                <AlertCircle className="w-4 h-4" /> {err}
              </div>
            ) : null}

            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit-btn"
              className="w-full h-12 rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold"
            >
              {busy ? "Signing in..." : "Log In"}
            </Button>

            <div className="relative py-2">
              <div className="absolute inset-x-0 top-1/2 border-t border-[#2A2A2A]"></div>
              <div className="relative flex justify-center">
                <span className="bg-[#0A0A0A] px-3 overline text-zinc-500">OR</span>
              </div>
            </div>
            <GoogleButton role="sender" testId="google-login-btn" label="Continue with Google" />

            <div className="text-sm text-zinc-500 text-center pt-2">
              New to NorthLine?{" "}
              <Link to="/register" data-testid="login-register-link" className="text-[#FF5A00] font-semibold hover:underline">
                Create an account
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
